// Complete Theme Configuration - All 6 Theme Variants
export type ThemeName = 'pure-white' | 'sky-light' | 'cloud-white' | 'ocean-breeze' | 'sunset-glow' | 'arctic-fresh' | 'neutral-elegance';

export interface ThemeColors {
  // Main Background
  background: string;
  // Surface/Cards
  surface: string;
  card: string;
  // Section Backgrounds
  sectionBackground: string;
  // Borders
  border: string;
  // Input Backgrounds
  input: string;
  // Text Colors (usually stay the same)
  text: string;
  textSecondary: string;
  textLight: string;
  // Brand Colors (unchanged)
  primary: string;
  primaryDark: string;
  primaryLight: string;
  secondary: string;
  secondaryDark: string;
  secondaryLight: string;
}

export interface Theme {
  name: ThemeName;
  displayName: string;
  description: string;
  colors: ThemeColors;
}

export const themes: Record<ThemeName, Theme> = {
  'pure-white': {
    name: 'pure-white',
    displayName: 'Pure White',
    description: 'Original clean white theme - pure white backgrounds throughout',
    colors: {
      background: '#FFFFFF',
      surface: '#FFFFFF',
      card: '#FFFFFF',
      sectionBackground: '#FFFFFF',
      border: '#e5e7eb',
      input: '#f9fafb',
      text: '#000000',
      textSecondary: '#6b7280',
      textLight: '#9ca3af',
      primary: '#2563eb',
      primaryDark: '#1e40af',
      primaryLight: '#3b82f6',
      secondary: '#FF6B35',
      secondaryDark: '#E55A2B',
      secondaryLight: '#FF8C5A',
    },
  },
  'sky-light': {
    name: 'sky-light',
    displayName: 'Sky Light',
    description: 'Clean, modern, professional - complements your blue brand',
    colors: {
      background: '#F0F4FF',
      surface: '#FFFFFF',
      card: '#FFFFFF',
      sectionBackground: '#EFF6FF',
      border: '#DBEAFE',
      input: '#F8FAFC',
      text: '#000000',
      textSecondary: '#6b7280',
      textLight: '#9ca3af',
      primary: '#2563eb',
      primaryDark: '#1e40af',
      primaryLight: '#3b82f6',
      secondary: '#FF6B35',
      secondaryDark: '#E55A2B',
      secondaryLight: '#FF8C5A',
    },
  },
  'cloud-white': {
    name: 'cloud-white',
    displayName: 'Cloud White',
    description: 'Neutral, minimal, versatile - works with any product type',
    colors: {
      background: '#FAFAFA',
      surface: '#FFFFFF',
      card: '#FFFFFF',
      sectionBackground: '#F5F5F5',
      border: '#E5E7EB',
      input: '#F9FAFB',
      text: '#000000',
      textSecondary: '#6b7280',
      textLight: '#9ca3af',
      primary: '#2563eb',
      primaryDark: '#1e40af',
      primaryLight: '#3b82f6',
      secondary: '#FF6B35',
      secondaryDark: '#E55A2B',
      secondaryLight: '#FF8C5A',
    },
  },
  'ocean-breeze': {
    name: 'ocean-breeze',
    displayName: 'Ocean Breeze',
    description: 'Modern, tech-forward - contemporary feel with good contrast',
    colors: {
      background: '#F1F5F9',
      surface: '#FFFFFF',
      card: '#FFFFFF',
      sectionBackground: '#E2E8F0',
      border: '#CBD5E1',
      input: '#F8FAFC',
      text: '#000000',
      textSecondary: '#6b7280',
      textLight: '#9ca3af',
      primary: '#2563eb',
      primaryDark: '#1e40af',
      primaryLight: '#3b82f6',
      secondary: '#FF6B35',
      secondaryDark: '#E55A2B',
      secondaryLight: '#FF8C5A',
    },
  },
  'sunset-glow': {
    name: 'sunset-glow',
    displayName: 'Sunset Glow',
    description: 'Warm, inviting, energetic - complements your orange accent',
    colors: {
      background: '#FFF7ED',
      surface: '#FFFFFF',
      card: '#FFFFFF',
      sectionBackground: '#FFEDD5',
      border: '#FED7AA',
      input: '#FFFBEB',
      text: '#000000',
      textSecondary: '#6b7280',
      textLight: '#9ca3af',
      primary: '#2563eb',
      primaryDark: '#1e40af',
      primaryLight: '#3b82f6',
      secondary: '#FF6B35',
      secondaryDark: '#E55A2B',
      secondaryLight: '#FF8C5A',
    },
  },
  'arctic-fresh': {
    name: 'arctic-fresh',
    displayName: 'Arctic Fresh',
    description: 'Fresh, clean, vibrant - bright aesthetic with blue brand',
    colors: {
      background: '#F0F9FF',
      surface: '#FFFFFF',
      card: '#FFFFFF',
      sectionBackground: '#E0F2FE',
      border: '#BAE6FD',
      input: '#F8FAFC',
      text: '#000000',
      textSecondary: '#6b7280',
      textLight: '#9ca3af',
      primary: '#2563eb',
      primaryDark: '#1e40af',
      primaryLight: '#3b82f6',
      secondary: '#FF6B35',
      secondaryDark: '#E55A2B',
      secondaryLight: '#FF8C5A',
    },
  },
  'neutral-elegance': {
    name: 'neutral-elegance',
    displayName: 'Neutral Elegance',
    description: 'Professional, timeless, versatile - works with all categories',
    colors: {
      background: '#F9FAFB',
      surface: '#FFFFFF',
      card: '#FFFFFF',
      sectionBackground: '#F3F4F6',
      border: '#E5E7EB',
      input: '#FFFFFF',
      text: '#000000',
      textSecondary: '#6b7280',
      textLight: '#9ca3af',
      primary: '#2563eb',
      primaryDark: '#1e40af',
      primaryLight: '#3b82f6',
      secondary: '#FF6B35',
      secondaryDark: '#E55A2B',
      secondaryLight: '#FF8C5A',
    },
  },
};

export const defaultTheme: ThemeName = 'pure-white';

export const getTheme = (themeName: ThemeName): Theme => {
  return themes[themeName] || themes[defaultTheme];
};

